/**
 * Color utility functions for consistent color manipulation across applications.
 * Used for AG Grid cell backgrounds, status colors, and other UI elements.
 */

/**
 * Convert hex color to rgba with specified alpha.
 * Supports #RGB, #RRGGBB, rgb(), and rgba() formats.
 *
 * This utility is essential for AG Grid best practices:
 * - Semi-transparent cell backgrounds allow row selection to show through
 * - Consistent color handling across light/dark themes
 *
 * @param hex - Color in hex (#RGB, #RRGGBB), rgb(), or rgba() format
 * @param alpha - Alpha value between 0 and 1
 * @returns rgba() color string
 *
 * @example
 * hexToRgba('#7b61ff', 0.6) // => 'rgba(123, 97, 255, 0.6)'
 * hexToRgba('#FFF', 0.5)     // => 'rgba(255, 255, 255, 0.5)'
 * hexToRgba('rgb(255, 0, 0)', 0.8) // => 'rgba(255, 0, 0, 0.8)'
 */
export function hexToRgba(hex: string, alpha: number): string {
  const fallback = `rgba(128, 128, 128, ${alpha})`;

  // Handle falsy input
  if (!hex) return fallback;

  // Handle already rgba colors - preserve original
  if (hex.startsWith("rgba")) return hex;

  // Handle rgb() colors - convert to rgba()
  if (hex.startsWith("rgb(")) {
    return hex.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
  }

  // Remove # if present and validate hex format
  const cleanHex = hex.replace("#", "");
  const hexRegex = /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/;
  if (!hexRegex.test(cleanHex)) return fallback;

  // Handle shorthand hex (e.g., #FFF -> #FFFFFF)
  let expandedHex = cleanHex;
  if (cleanHex.length === 3) {
    expandedHex = cleanHex
      .split("")
      .map((c) => c + c)
      .join("");
  }

  const r = parseInt(expandedHex.substring(0, 2), 16);
  const g = parseInt(expandedHex.substring(2, 4), 16);
  const b = parseInt(expandedHex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Mix a color with transparent at specified percentage.
 * Browser-safe alternative to CSS `color-mix(in srgb, <color> X%, transparent)`.
 *
 * @param color - Color in hex (#RGB, #RRGGBB), rgb(), or rgba() format
 * @param percentage - Percentage of color opacity (0-100)
 * @returns rgba() color string
 *
 * @example
 * colorMixTransparent('#7b61ff', 15) // => 'rgba(123, 97, 255, 0.15)'
 * colorMixTransparent('rgb(255, 0, 0)', 25) // => 'rgba(255, 0, 0, 0.25)'
 */
export function colorMixTransparent(color: string, percentage: number): string {
  const alpha = Math.max(0, Math.min(100, percentage)) / 100;
  return hexToRgba(color, alpha);
}

/**
 * Parse CSS variable value or return fallback.
 * Useful for extracting color values from CSS custom properties.
 *
 * @param cssVar - CSS variable reference (e.g., 'var(--color-primary-500)')
 * @param fallbackHex - Fallback hex color if variable cannot be resolved
 * @returns Hex color string
 */
export function resolveCssColorVar(cssVar: string, fallbackHex: string): string {
  if (!cssVar.startsWith("var(")) return cssVar;

  // In browser context, try to resolve the CSS variable
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    const varName = cssVar.replace(/var\(([^)]+)\)/, "$1").trim();
    const computed = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    if (computed) return computed;
  }

  return fallbackHex;
}

